/**
 * Format timestamp contextually for message display
 * P1 Milestone B: Message-level timestamp formatting
 */
export function formatMessageTimestamp(timestamp: string | Date): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp
  const now = new Date()
  const messageDate = new Date(date)

  // Check if same day
  const isSameDay = messageDate.toDateString() === now.toDateString()

  // Check if yesterday
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday = messageDate.toDateString() === yesterday.toDateString()

  const timeString = messageDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })

  if (isSameDay) {
    return timeString // "3:13 PM"
  } else if (isYesterday) {
    return `Yesterday · ${timeString}` // "Yesterday · 9:42 AM"
  } else {
    const dateString = messageDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
    return `${dateString} · ${timeString}` // "Jan 12 · 3:13 PM"
  }
}

/**
 * Check if timestamp should be shown based on gap from previous message
 * P1 Milestone B: Reduce timestamp repetition
 */
export function shouldShowTimestamp(currentTimestamp: string, previousTimestamp?: string, forceShow = false): boolean {
  if (forceShow) return true
  if (!previousTimestamp) return true

  const current = new Date(currentTimestamp)
  const previous = new Date(previousTimestamp)

  // Always show if different day
  if (current.toDateString() !== previous.toDateString()) {
    return true
  }

  // Show if gap > 60 minutes
  const gapMinutes = (current.getTime() - previous.getTime()) / (1000 * 60)
  return gapMinutes > 60
}

/**
 * P1 Milestone B: Humanized timestamp system
 * Computes relative temporal labels at render time from created_at
 * Source of truth: message.created_at (or messages.created_at) from DB
 */

/**
 * Format timestamp with humanized relative labels computed at view time
 * Rules:
 * - Same calendar day: "Today · h:mm A"
 * - Yesterday: "Yesterday · h:mm A"
 * - Within last 7 days: "dddd · h:mm A" (e.g., "Tuesday · 2:09 PM")
 * - 7-30 days: "N weeks ago"
 * - 30+ days or different year: "MMM D, YYYY"
 */
export function formatHumanizedTimestamp(timestamp: string | Date): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp
  const now = new Date()

  // Get local dates for calendar-day comparison
  const messageDate = new Date(date.toDateString())
  const todayDate = new Date(now.toDateString())

  // Calculate day difference
  const daysDiff = Math.floor((todayDate.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24))

  // Format time consistently
  const timeString = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })

  // Same calendar day (local)
  if (daysDiff === 0) {
    console.log("[v0] TIMESTAMP_SYSTEM: Same day detected", { timestamp, formatted: `Today · ${timeString}` })
    return `Today · ${timeString}`
  }

  // Yesterday (local)
  if (daysDiff === 1) {
    console.log("[v0] TIMESTAMP_SYSTEM: Yesterday detected", { timestamp, formatted: `Yesterday · ${timeString}` })
    return `Yesterday · ${timeString}`
  }

  // Within last 7 days: day name
  if (daysDiff > 1 && daysDiff < 7) {
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" })
    console.log("[v0] TIMESTAMP_SYSTEM: Within 7 days", { daysDiff, formatted: `${dayName} · ${timeString}` })
    return `${dayName} · ${timeString}`
  }

  // 7-30 days: weeks ago
  if (daysDiff >= 7 && daysDiff < 30) {
    const weeks = Math.max(1, Math.round(daysDiff / 7))
    const label = weeks === 1 ? "1 week ago" : `${weeks} weeks ago`
    console.log("[v0] TIMESTAMP_SYSTEM: Weeks ago", { daysDiff, weeks, formatted: label })
    return label
  }

  // 30+ days or different year: full date
  const currentYear = now.getFullYear()
  const messageYear = date.getFullYear()

  if (messageYear !== currentYear) {
    const fullDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    console.log("[v0] TIMESTAMP_SYSTEM: Different year", { formatted: fullDate })
    return fullDate
  }

  // Same year, 30+ days ago
  const shortDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
  console.log("[v0] TIMESTAMP_SYSTEM: 30+ days same year", { formatted: shortDate })
  return shortDate
}

/**
 * Get absolute timestamp for hover tooltip
 */
export function formatAbsoluteTimestamp(timestamp: string | Date): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}
